import { Icon, Loader } from '@pwc/pwc-deals-ui';
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { PowerPointIngestButton } from './PowerPointIngestButton';
import {
  IngestionErrorsProvider,
  useIngestionErrors,
} from './IngestionErrorProvider';
import { PowerPointFileName } from './PowerPointFileName';
import PowerPointGroupName from './PowerPointGroupName';
import './PowerPointIngestionModalBody.scss';
import PowerPointSlideNumber from './PowerPointSlideNumber';

const generateGroupNameOptions = (groupNames) =>
  groupNames.map((name) => ({ value: name, key: name }));

const getGroupNameOptionsPerSlide = (slides, ingestedGroups, driveItemId) => {
  const groupsForFile = ingestedGroups
    ? ingestedGroups.filter((group) => group.driveItemId === driveItemId)
    : [];

  const alreadyIngested = groupsForFile.reduce((dictionary, group) => {
    const { slideNumber, groupName } = group;
    const groupsForSlideNumber = dictionary[slideNumber] || [];
    return {
      ...dictionary,
      [slideNumber]: [...groupsForSlideNumber, groupName],
    };
  }, {});

  const options = slides.reduce((dictionary, slide) => {
    const { slideNumber } = slide;
    const availableGroupNames = alreadyIngested[slideNumber]
      ? slide.groupNames.filter(
          (name) => !alreadyIngested[slideNumber].includes(name)
        )
      : slide.groupNames;

    const ingestedOptionsForSlideCount = alreadyIngested[slideNumber]
      ? alreadyIngested[slideNumber].length
      : 0;
    const allOptionsAlreadyIngested =
      ingestedOptionsForSlideCount === slide.groupNames.length &&
      slide.groupNames.length !== 0;

    return {
      ...dictionary,
      [slideNumber]: {
        allOptionsAlreadyIngested,
        options: generateGroupNameOptions(availableGroupNames),
      },
    };
  }, {});

  return options;
};

const IngestionErrorMessage = () => {
  const [errorState] = useIngestionErrors();
  return (
    <>
      {errorState.ingestionError && (
        <span
          className="a-form-error"
          data-testid="ingest-powerpoint-error-message"
        >
          <Icon
            name="alert"
            className="fill icon-peach ingestion-alert"
            width="16"
          />
          {errorState.ingestionError}
        </span>
      )}
    </>
  );
};

export const PowerPointIngestionModalBody = ({
  handleSuccessfulIngestion,
  ingestedPowerPointGroups,
}) => {
  const [fileName, setFileName] = useState('');
  const [pptFiles, setPptFiles] = useState(null);
  const [selectedFileOption, setSelectedFileOption] = useState(undefined);
  const [sharePointItemUrl, setSharePointItemUrl] = useState('');
  const [slideNumber, setSlideNumber] = useState('');
  const [slides, setSlides] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [groupNames, setGroupNames] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [isSlideListLoading, setIsSlideListLoading] = useState(false);
  const { projectId } = useParams();

  useEffect(() => {
    if (slides && slides.length > 0) {
      setGroupNames(
        getGroupNameOptionsPerSlide(
          slides,
          ingestedPowerPointGroups,
          selectedFileOption.driveItemId
        )
      );
    }
  }, [ingestedPowerPointGroups, selectedFileOption, slides]);

  return (
    <IngestionErrorsProvider>
      <Modal.Body>
        {isLoading ? (
          <Loader dataTestId="powerpoint-file-list-loader" />
        ) : (
          <>
            <PowerPointFileName
              fileName={fileName}
              pptFiles={pptFiles}
              setFileName={setFileName}
              setSlideNumber={setSlideNumber}
              setGroupName={setGroupName}
              setSelectedFileOption={setSelectedFileOption}
              projectId={projectId}
              setPptFiles={setPptFiles}
              setIsLoading={setIsLoading}
            />
            <PowerPointSlideNumber
              pptFiles={pptFiles}
              fileName={fileName}
              slideNumber={slideNumber}
              setSlideNumber={setSlideNumber}
              setSharePointItemUrl={setSharePointItemUrl}
              projectId={projectId}
              setGroupName={setGroupName}
              ingestedPowerPointGroups={ingestedPowerPointGroups}
              setSlides={setSlides}
              selectedFileOption={selectedFileOption}
              setIsSlideListLoading={setIsSlideListLoading}
              isSlideListLoading={isSlideListLoading}
            />
            <PowerPointGroupName
              groupName={groupName}
              setGroupName={setGroupName}
              groupNames={groupNames}
              slideNumber={slideNumber}
              isSlideListLoading={isSlideListLoading}
            />
            <IngestionErrorMessage />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="modal-actions">
          <PowerPointIngestButton
            driveItemId={
              selectedFileOption ? selectedFileOption.driveItemId : ''
            }
            driveId={selectedFileOption ? selectedFileOption.driveId : ''}
            fileName={fileName}
            slideNumber={slideNumber}
            groupName={groupName}
            loading={isLoading}
            handleSuccessfulIngestion={handleSuccessfulIngestion}
            projectId={projectId}
            sharePointItemUrl={sharePointItemUrl}
            setIsLoading={setIsLoading}
            setSlideNumber={setSlideNumber}
            setGroupName={setGroupName}
          />
        </div>
      </Modal.Footer>
    </IngestionErrorsProvider>
  );
};
